/**
 * BMT Companion: a trainee's sign-up, dates, check-in state and homework for BMT,
 * served by hogc-one-api under /api/v2/training/bmt.
 */

// The string is the contract with hogc-one-api (BMT_COMPANION_FLAG). The API 404s
// every BMT route while it is off; the app hides the home card and guards /bmt.
export const BMT_COMPANION_FLAG = 'bmt-companion';

/** The scanner type the API mints BMT check-in QRs with: https://hogc.one/scan/bmt/checkin/{token}. */
export const BMT_SCAN_TYPE = 'bmt';

/** The trainee routes, relative to VUE_APP_API_URL (which already ends in /api). */
export const BMT_API_PATH = '/v2/training/bmt';

/** Re-read /me on page enter or app resume only when the last read is older than this. */
export const BMT_ME_STALE_MS = 60 * 1000;

/** Where an unsent TEXT homework answer is kept on the device, one key per homework id. */
export const BMT_DRAFT_KEY_PREFIX = 'bmt-homework-draft:';

export const BMT_HUB_PATH = '/bmt';
export const BMT_SCHEDULE_PATH = '/bmt/schedule';
export const BMT_RESOURCES_PATH = '/bmt/resources';
export const BMT_TASKS_PATH = '/bmt/tasks';
/** A written task (the testimony), full screen. */
export const bmtTaskPath = id => `${BMT_TASKS_PATH}/${id}`;

/** Who each course is for, under its name on the hub and its pages. */
export const BMT_COURSE_AUDIENCE = Object.freeze({
  BMT: 'For Pre-Uni & above',
  'BMT Youth': 'For Youth & JC',
});

export const BMT_RESOURCE_KIND = Object.freeze({
  VIDEO: 'VIDEO',
  FILE: 'FILE',
  LINK: 'LINK',
});

/** A written task saves its draft this long after typing stops. */
export const BMT_DRAFT_SAVE_MS = 2000;

/** Where a BMT check-in scan lands with its result (bmt.lastCheckIn). */
export const BMT_CHECK_IN_PATH = '/bmt/check-in';

export const BMT_ENROLMENT_STATUS = Object.freeze({
  PENDING: 'PENDING',
  APPROVED: 'APPROVED',
  WITHDRAWN: 'WITHDRAWN',
});

export const BMT_HOMEWORK_TYPE = Object.freeze({
  TICK: 'TICK',
  TEXT: 'TEXT',
});

/** The `cgNote` sent when the member says they are not in a Connect Group yet. */
export const BMT_CG_NOTE_NOT_YET = 'Not yet';

/** Before the CG name in the `cgNote` sent when the member says they are in one. */
export const BMT_CG_NOTE_YES_PREFIX = 'Yes: ';

/** The API caps cgNote at 200 characters; a written task is held to 5,000 here. */
export const BMT_CG_NOTE_MAX = 200;
export const BMT_TEXT_MAX = 5000;

/** Hub sub-pages added with the 3 Nov designs. */
export const BMT_SIGNED_UP_PATH = '/bmt/signed-up';
export const BMT_ATTENDANCE_PATH = '/bmt/attendance';
export const BMT_MAKE_UP_PATH = '/bmt/make-up';
export const BMT_CLOSED_PATH = '/bmt/closed';
export const BMT_REFLECTION_PATH = '/bmt/reflection';

/** What a homework item is, which picks its screen and how it counts as done. */
export const BMT_HOMEWORK_KIND = Object.freeze({
  GENERAL: 'GENERAL',
  MINISTRY_APPLICATION: 'MINISTRY_APPLICATION',
  TESTIMONY: 'TESTIMONY',
  VIDEO: 'VIDEO',
});

/** A make-up for a missed date: none open yet, dates to pick from, or one picked. */
export const BMT_MAKE_UP_STATUS = Object.freeze({
  WAITING: 'waiting',
  OPEN: 'open',
  BOOKED: 'booked',
});

/**
 * The testimony's four sections, from the GenerationS University Conference 2026
 * testimony template (given by the BMT team, 31 Oct), copied word for word.
 */
export const BMT_TESTIMONY_SECTIONS = Object.freeze([
  {
    title: 'Before Knowing God',
    questions: [
      'Describe how you were before your relationship with Jesus',
      'How were you like? What were some struggles you had?',
      'Give examples so that your friend listening to you can relate.',
    ],
  },
  {
    title: 'Transition: Coming to Church / Becoming a Christian',
    questions: [
      'How did you come to church? What kept you coming back?',
      'What made you decide to become a Christian / recommit your life to Christ?',
      'Describe your encounter with God.',
    ],
  },
  {
    title: 'After becoming a Christian…',
    questions: [
      'How did your life change after becoming a Christian? Share examples to show the difference from how you were like before knowing God.',
      'If you want, you can share any struggle you went through and how it’s different overcoming it with God.',
      'You can also share what you love about being a Christian and being part of the church.',
    ],
  },
  {
    title: 'Closing (1-2 lines)',
    questions: [
      'Can you think of one line that can encapsulate what God has done in your life?',
      'What do you want the people listening to know about having a relationship with God?',
    ],
  },
]);

export const BMT_TESTIMONY_TIPS = Object.freeze([
  'Be real, personal and organic.',
  'Use language that is relevant for your friend. e.g., Words like “fellowship”, “altar call”, “quiet time”, might not make sense to a non-Christian.',
  'People remember how you made them feel, more than facts you tell them. e.g., Describing the moment you felt the most insecure could relate more than saying “I was insecure and always compared myself to others”.',
  'Keep it short – aim for 2-3 minutes.',
]);

/** The Reflection's areas, each tied to the sessions that speak to it (3 Nov designs). */
export const BMT_REFLECTION_AREAS = Object.freeze([
  { key: 'serving', title: 'Serving', from: 'Intro to BMT · Destiny Cues' },
  { key: 'atmosphere', title: 'Building the atmosphere', from: 'Atmosphere Architect' },
  { key: 'reaching-out', title: 'Reaching out', from: 'i3 · Wisdom on Sharing Good News' },
  {
    key: 'generations',
    title: 'Building across generations',
    from: 'Power of a 3-Generation Church',
  },
  { key: 'giving', title: 'Giving', from: '5 Truths on Giving' },
  { key: 'something-else', title: 'Something else', from: 'Anything on your heart' },
]);

export const BMT_REFLECTION_STARTERS = Object.freeze([
  "I'd like to…",
  'One thing I could do is…',
  "I'm still figuring out…",
]);

export const BMT_REFLECTION_TEXT_MAX = 2000;
